import { useState, useMemo } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useBranch } from "@/context/BranchContext";
import ProfileMenu from "@/components/ProfileMenu";
import { Button } from "@/components/ui/button";
import {
  Users,
  Contact,
  Scissors,
  Wallet,
  CalendarDays,
  FileSpreadsheet,
  BarChart3,
  Settings,
  ArrowLeft,
  PanelLeftClose,
  PanelLeft,
  Menu,
  X,
} from "lucide-react";

const adminNav = [
  { path: "/admin/accounts", label: "Staff", icon: Users },
  { path: "/admin/clients", label: "Clients", icon: Contact },
  { path: "/admin/services", label: "Services", icon: Scissors },
  { path: "/admin/payroll", label: "Payroll", icon: Wallet },
  { path: "/admin/roster", label: "Roster", icon: CalendarDays },
  { path: "/admin/cashflow", label: "Cash Flow", icon: FileSpreadsheet },
  { path: "/admin/analytics", label: "Analytics", icon: BarChart3 },
  { path: "/admin/settings", label: "Settings", icon: Settings },
];

export default function AdminLayout() {
  const { selectedBranchId, branches, currentBranch } = useBranch();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();

  const outletContext = useMemo(
    () => ({ selectedBranch: selectedBranchId, branches }),
    [selectedBranchId, branches]
  );

  const sidebar = (
    <div className="flex flex-col h-full bg-forest text-white">
      <div className={`flex items-center gap-2 px-4 py-5 border-b border-white/10 ${collapsed ? "justify-center px-2" : ""}`}>
        {!collapsed && (
          <div className="leading-tight">
            <h1 className="font-heading font-bold text-base">Admin Console</h1>
            <p className="text-[11px] text-white/60">{currentBranch?.name || "Management"}</p>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="ml-auto text-white/80 hover:text-white hover:bg-white/10 hidden lg:flex"
          onClick={() => setCollapsed((c) => !c)}
          data-testid="admin-sidebar-collapse"
        >
          {collapsed ? <PanelLeft className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
        </Button>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto" data-testid="admin-nav">
        {adminNav.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            title={item.label}
            onClick={() => setMobileOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors
              ${collapsed ? "justify-center" : ""}
              ${isActive ? "bg-white text-forest" : "text-white/80 hover:bg-white/10 hover:text-white"}`
            }
            data-testid={`admin-nav-${item.path.split("/").pop()}`}
          >
            <item.icon className="h-5 w-5 shrink-0" />
            {!collapsed && item.label}
          </NavLink>
        ))}
      </nav>

      <div className="p-3 border-t border-white/10">
        <Button
          className={`w-full bg-ochre hover:bg-ochre/90 text-white ${collapsed ? "px-0" : "justify-start"}`}
          onClick={() => navigate("/worksheet")}
          title="Back to work sheet"
          data-testid="back-to-worksheet-btn"
        >
          <ArrowLeft className="h-4 w-4 shrink-0" />
          {!collapsed && <span className="ml-2">Back to work sheet</span>}
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-cream">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
          data-testid="admin-sidebar-overlay"
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 transform transition-all duration-300 ease-out
          ${collapsed ? "lg:w-16" : "lg:w-60"} w-60
          ${mobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}
        data-testid="admin-sidebar"
      >
        {sidebar}
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 glass-effect border-b border-border">
          <div className="flex items-center justify-between px-4 lg:px-6 py-3">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setMobileOpen(true)}
                data-testid="admin-open-sidebar"
              >
                <Menu className="h-5 w-5" />
              </Button>
              <h2 className="font-heading font-semibold text-forest text-lg">Admin Console</h2>
            </div>
            <ProfileMenu />
          </div>
        </header>

        <main className="flex-1 overflow-auto p-4 lg:p-6" data-testid="admin-content">
          <Outlet context={outletContext} />
        </main>
      </div>
    </div>
  );
}
